import React, { useEffect, useState } from 'react';
import { getLeads, updateLead, deleteLead } from '../lib/firebase';

const STATUSES = ['all', 'new', 'contacted', 'qualified', 'closed', 'cold'];

function statusBadge(s) {
  return <span className={`badge badge-${s || 'new'}`}>{s || 'new'}</span>;
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState('contacted');

  async function load() {
    setLoading(true);
    try {
      const data = await getLeads({});
      setLeads(data);
    } catch (e) {
      console.error('Failed to load leads:', e);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  const categories = ['all', ...Array.from(new Set(leads.map(l => l.category).filter(Boolean))).sort()];

  const filtered = leads.filter(l => {
    const matchesStatus = statusFilter === 'all' || (l.status || 'new') === statusFilter;
    const matchesCategory = categoryFilter === 'all' || l.category === categoryFilter;
    if (!matchesStatus || !matchesCategory) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      (l.businessName || '').toLowerCase().includes(s) ||
      (l.contactName || '').toLowerCase().includes(s) ||
      (l.suburb || '').toLowerCase().includes(s) ||
      (l.category || '').toLowerCase().includes(s) ||
      (l.phone || '').toLowerCase().includes(s)
    );
  });

  const allChecked = filtered.length > 0 && filtered.every(l => checkedIds.has(l.id));

  const counts = {
    total: leads.length,
    new: leads.filter(l => (l.status || 'new') === 'new').length,
    contacted: leads.filter(l => l.status === 'contacted').length,
    qualified: leads.filter(l => l.status === 'qualified').length,
  };

  function toggleAll() {
    if (allChecked) {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(l => next.delete(l.id));
        return next;
      });
    } else {
      setCheckedIds(prev => {
        const next = new Set(prev);
        filtered.forEach(l => next.add(l.id));
        return next;
      });
    }
  }

  function toggleOne(id) {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleStatusChange(id, status) {
    await updateLead(id, { status });
    setSelected(prev => prev ? { ...prev, status } : prev);
    setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this lead?')) return;
    await deleteLead(id);
    setSelected(null);
    setLeads(prev => prev.filter(l => l.id !== id));
  }

  async function handleBulkStatus() {
    const ids = Array.from(checkedIds);
    await Promise.all(ids.map(id => updateLead(id, { status: bulkStatus })));
    setLeads(prev => prev.map(l => checkedIds.has(l.id) ? { ...l, status: bulkStatus } : l));
    setCheckedIds(new Set());
  }

  async function handleBulkDelete() {
    if (!window.confirm(`Delete ${checkedIds.size} lead${checkedIds.size > 1 ? 's' : ''}?`)) return;
    const ids = Array.from(checkedIds);
    await Promise.all(ids.map(id => deleteLead(id)));
    setLeads(prev => prev.filter(l => !checkedIds.has(l.id)));
    setCheckedIds(new Set());
  }

  function cleanPhone(raw) {
    if (!raw) return '—';
    if (raw.toLowerCase().includes('send to phone')) return '—';
    return raw.replace(/^Phone:\s*/i, '').trim() || '—';
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">▸ pipeline / active</div>
        <div className="page-title">leads</div>
        <div className="page-divider" />
      </div>

      <div className="stats-row">
        <div className="stat-box corner-mark">
          <div className="stat-num">{counts.total}</div>
          <div className="stat-label">total</div>
        </div>
        <div className="stat-box">
          <div className={`stat-num${counts.new > 0 ? ' hot' : ''}`}>{counts.new}</div>
          <div className="stat-label">new</div>
        </div>
        <div className="stat-box">
          <div className="stat-num">{counts.contacted}</div>
          <div className="stat-label">contacted</div>
        </div>
        <div className="stat-box">
          <div className="stat-num">{counts.qualified}</div>
          <div className="stat-label">qualified</div>
        </div>
      </div>

      <div className="filter-row">
        <input
          placeholder="search name, business, suburb, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          {STATUSES.map(s => <option key={s} value={s}>{s === 'all' ? 'all statuses' : s}</option>)}
        </select>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
          {categories.map(c => <option key={c} value={c}>{c === 'all' ? 'all categories' : c}</option>)}
        </select>
        <span className="filter-count">{filtered.length}</span>
      </div>

      {checkedIds.size > 0 && (
        <div className="bulk-bar">
          <span className="bulk-count">{checkedIds.size} selected</span>
          <select value={bulkStatus} onChange={e => setBulkStatus(e.target.value)}>
            {['new', 'contacted', 'qualified', 'closed', 'cold'].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button className="btn btn-sm" onClick={handleBulkStatus}>set status</button>
          <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>delete</button>
          <button className="btn btn-sm" onClick={() => setCheckedIds(new Set())}>clear</button>
        </div>
      )}

      {loading ? (
        <div className="empty">loading...</div>
      ) : filtered.length === 0 ? (
        <div className="empty">no leads. add one or run the scraper.</div>
      ) : (
        <table className="lead-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  className="lead-check"
                  checked={allChecked}
                  onChange={toggleAll}
                />
              </th>
              <th>business</th>
              <th>category</th>
              <th>suburb</th>
              <th>phone</th>
              <th>status</th>
              <th>source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(lead => (
              <tr
                key={lead.id}
                onClick={() => setSelected(lead)}
                className={checkedIds.has(lead.id) ? 'row-checked' : ''}
              >
                <td onClick={e => { e.stopPropagation(); toggleOne(lead.id); }}>
                  <input
                    type="checkbox"
                    className="lead-check"
                    checked={checkedIds.has(lead.id)}
                    onChange={() => toggleOne(lead.id)}
                  />
                </td>
                <td>
                  <div className="lead-name">{lead.businessName}</div>
                  {lead.contactName && <div className="lead-biz">{lead.contactName}</div>}
                </td>
                <td>{lead.category || '—'}</td>
                <td>{lead.suburb || '—'}</td>
                <td>{cleanPhone(lead.phone)}</td>
                <td>{statusBadge(lead.status)}</td>
                <td>
                  <span className={lead.source === 'scraper' ? 'source-scraper' : 'source-manual'}>
                    {lead.source || 'manual'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selected && (
        <div className="detail-overlay" onClick={() => setSelected(null)}>
          <div className="detail-drawer" onClick={e => e.stopPropagation()}>
            <div className="drawer-header">
              <div>
                <div className="drawer-name">{selected.businessName}</div>
                {selected.contactName && <div className="drawer-biz">{selected.contactName}</div>}
              </div>
              <button className="drawer-close" onClick={() => setSelected(null)}>▸ esc</button>
            </div>

            <div>
              {[
                ['category', selected.category],
                ['phone', cleanPhone(selected.phone) !== '—' ? cleanPhone(selected.phone) : null],
                ['email', selected.email],
                ['website', selected.website],
                ['address', selected.address],
                ['suburb', selected.suburb],
                ['rating', selected.rating ? `${selected.rating} ★` : null],
                ['reviews', selected.reviewCount],
                ['source', selected.source],
              ].filter(([, v]) => v).map(([k, v]) => (
                <div className="detail-row" key={k}>
                  <span className="detail-key">{k}</span>
                  <span className="detail-val">{v}</span>
                </div>
              ))}
            </div>

            {selected.notes && (
              <div>
                <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--smoke)', marginBottom: 8 }}>notes</div>
                <p style={{ fontSize: 12, color: 'var(--bone)', lineHeight: 1.6 }}>{selected.notes}</p>
              </div>
            )}

            <div>
              <div style={{ fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--smoke)', marginBottom: 8 }}>status</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['new', 'contacted', 'qualified', 'closed', 'cold'].map(s => (
                  <button
                    key={s}
                    className={`btn btn-sm ${selected.status === s ? 'btn-primary' : ''}`}
                    onClick={() => handleStatusChange(selected.id, s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '0.5px solid var(--hairline)' }}>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(selected.id)}>
                delete lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
